import React, { useEffect, useState, useRef } from "react";
import { TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  CircularProgress,
  FormHelperText,
  Select,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "react-quill/dist/quill.snow.css";
import CustomTypography from "../../globalStyles/fonts";
import { countryService } from "../../utils/services/allCountry";
import { isMobile } from "../../globalStyles/mobile";
import ClientAutocomplete from "../clientAutocomplete";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const ManageArchiveOrder = ({
  formData,
  handleClose,
  open,
  onClickAdd,
  formErrors,
  onChangeData,
  client,
}) => {
  const modalRef = useRef(null);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose && handleClose();
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="md"
      fullWidth
      ref={modalRef}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          <CustomTypography fontWeight={500} fontSize={isMobile() ? 16 : 24}>
            {`${formData?.id ? "Update" : "Create"} Historical Data`}
          </CustomTypography>
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 20 }} />

      <DialogContent
        ref={modalRef}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
        sx={{
          overflowY: "auto",
          "&::WebkitScrollbar": {
            width: "7px",
          },
          "&::WebkitScrollbar-track": {
            backgroundColor: "#D9D9D9",
            borderRadius: 14,
          },
          "&::WebkitScrollbar-thumb": {
            backgroundColor: "#273894",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#555",
            },
          },
        }}
      >
        <Grid container spacing={2}>
          {!client ? (
            <Grid item xs={12} sm={12}>
              <CustomTypography fontSize={15}>
                {`Client`}&nbsp;
                <span style={{ color: "red" }}>*</span>
              </CustomTypography>

              <ClientAutocomplete
                selectedClient={formData.client}
                onClientChange={(e) => onChangeData("client", e)}
                errorMessage={formErrors.client}
              />
              {formErrors.client && (
                <FormHelperText error>{formErrors.client}</FormHelperText>
              )}
            </Grid>
          ) : (
            <></>
          )}
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              {`Order ID`}&nbsp;
              <span style={{ color: "red" }}>*</span>
            </CustomTypography>

            <TextField
              sx={{ marginTop: 1, fontSize: 13, fontStyle: "Poppins" }}
              size="small"
              placeholder={`Enter Order ID`}
              variant="outlined"
              value={formData.order_id}
              onChange={(e) => onChangeData("order_id", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.order_id}
              helperText={formErrors.order_id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Order Date&nbsp;
              <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                sx={{ marginTop: 1 }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                value={formData.order_date ? dayjs(formData.order_date) : null}
                onChange={(date) =>
                  onChangeData(
                    "order_date",
                    date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "",
                  )
                }
              />
            </LocalizationProvider>
            {formErrors.order_date && (
              <FormHelperText error>{formErrors.order_date}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Item</CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              placeholder="Enter Item."
              variant="outlined"
              value={formData.item}
              onChange={(e) => onChangeData("item", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.item}
              helperText={formErrors.item}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Category</CustomTypography>
            <TextField
              placeholder="Enter Category"
              inputMode="numeric"
              sx={{ marginTop: 1 }}
              type="text"
              size="small"
              variant="outlined"
              value={formData.category}
              onChange={(e) => onChangeData("category", e.target.value)}
              fullWidth
              error={!!formErrors.category}
              helperText={formErrors.category}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>SKU</CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              placeholder="Enter SKU."
              variant="outlined"
              value={formData.sku}
              onChange={(e) => onChangeData("sku", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.sku}
              helperText={formErrors.sku}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Quantity</CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              placeholder="Enter Quantity."
              variant="outlined"
              value={formData.qty}
              onChange={(e) => onChangeData("qty", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.qty}
              helperText={formErrors.qty}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Size</CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              placeholder="Enter Size"
              variant="outlined"
              value={formData.size}
              onChange={(e) => onChangeData("size", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.size}
              helperText={formErrors.size}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <CustomTypography fontSize={15}>
              Currency <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <Select
              fullWidth
              value={formData.currency}
              onChange={(e) => onChangeData("currency", e.target.value)}
              size="small"
              sx={{ marginTop: 1 }}
            >
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={8} sm={4}>
            <CustomTypography fontSize={15}>
              Price <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              sx={{ marginTop: 1 }}
              size="small"
              placeholder="Enter Price."
              variant="outlined"
              value={formData.price}
              onChange={(e) => onChangeData("price", e.target.value)}
              fullWidth
              autoFocus
              error={!!formErrors.price}
              helperText={formErrors.price}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 15 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleClose()}
          // className="cancel-button-style"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={(e) => onClickAdd(e)}
          className="add-button-style"
          style={{ textTransform: "none" }}
        >
          {formData?.id ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageArchiveOrder;
